import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { isHubMedOnlyUser, isHolaOnlyUser } from "@/services/adminAuthority";
import { getChemistName } from "@/utils/textUtils";
import { IGNORE_PW_MANAGERS } from "@/utils/formHints";
import SearchIcon from "@mui/icons-material/Search";

const ChemistSearchBox = ({ sidebar = false }) => {
  const navigate = useNavigate();
  const isPHAdmin = sessionStorage.getItem("IsPHAdmin") === "true";

  const [options, setOptions] = useState([]);
  const [fetched, setFetched] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  if (isHubMedOnlyUser() || isHolaOnlyUser()) return null;

  const loadOptions = async () => {
    if (fetched) return;
    setFetched(true);
    try {
      if (isPHAdmin) {
        const res = await phappsRequest(API_PATHS.CHEMIST_LIST, "GET");
        const list = res?.chemists ?? [];
        setOptions(
          list
            .map((c) => ({ label: c.chemistName ?? c.ChemistName ?? "", uuid: c.PHUUID ?? "" }))
            .filter((o) => o.label && o.uuid)
        );
      } else {
        const res = await phappsRequest(API_PATHS.MANAGER_CHEMIST_LIST, "GET");
        const list = res?.chemists ?? [];
        setOptions(
          list
            .map((c) => ({ label: c.ChemistName ?? "", uuid: c.ChemistUUID ?? "" }))
            .filter((o) => o.label && o.uuid)
        );
      }
    } catch {
      // fail silently
    }
  };

  const q = query.trim().toLowerCase();
  const filtered =
    q.length < 1
      ? []
      : options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 10);

  const handleSelect = (option) => {
    const path = isPHAdmin
      ? `/admin-functions/chemist/${encodeURIComponent(option.uuid)}`
      : `/manager-functions/chemist/${encodeURIComponent(option.uuid)}`;
    navigate(path);
    setQuery("");
    setOpen(false);
  };

  return (
    <div
      className="relative"
      style={sidebar ? { width: "100%" } : { minWidth: 220, maxWidth: 300 }}
    >
      <div
        className="flex items-center rounded-lg px-2.5 py-1.5 gap-1.5 transition-colors"
        style={
          sidebar
            ? {
                backgroundColor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.15)",
              }
            : {
                backgroundColor: "#f9fafb",
                border: "1px solid #d1d5db",
              }
        }
      >
        <SearchIcon
          sx={{ fontSize: 15, color: sidebar ? "rgba(255,255,255,0.5)" : "#9ca3af", flexShrink: 0 }}
        />
        <input
          ref={inputRef}
          type="text"
          placeholder="Find chemist…"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { loadOptions(); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          {...IGNORE_PW_MANAGERS}
          className="flex-1 text-sm outline-none bg-transparent min-w-0 leading-5"
          style={
            sidebar
              ? { color: "white", caretColor: "white" }
              : { color: "#374151" }
          }
        />
        {query && (
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setQuery(""); inputRef.current?.focus(); }}
            tabIndex={-1}
            style={{ color: sidebar ? "rgba(255,255,255,0.4)" : "#9ca3af", lineHeight: 1 }}
          >
            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto"
          style={{ minWidth: "100%", maxHeight: 260, zIndex: 9999 }}
        >
          {filtered.map((o) => (
            <button
              key={o.uuid}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(o)}
              className="w-full text-left px-3 py-2 text-sm text-gray-800 hover:bg-blue-50 hover:text-blue-700 transition-colors border-b border-gray-100 last:border-b-0"
            >
              {getChemistName(o.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChemistSearchBox;
